import { Link, useNavigate } from "react-router-dom";
import { BugIcon, PlusIcon } from "lucide-react";

import { useProjects } from "../app/queries";
import { Topbar } from "../components/Topbar";
import { Button } from "../components/ui/button";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "../components/ui/empty";
import { Skeleton } from "../components/ui/skeleton";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Badge } from "../components/ui/badge";
import {
  Page,
  PageBody,
  PageDescription,
  PageHeader,
  PageTitle,
} from "../components/PageLayout";
import { shortDate } from "../lib/format";
import type { Project } from "../lib/api";

export function ProjectsHomePage() {
  const navigate = useNavigate();
  const projects = useProjects();
  const items = projects.data?.projects ?? [];

  return (
    <Page>
      <Topbar title="All projects" />
      <PageHeader>
        <div className="flex flex-row flex-wrap items-end justify-between gap-3">
          <div>
            <PageTitle>Projects</PageTitle>
            <PageDescription>
              Manage your games and incoming bug reports.
            </PageDescription>
          </div>
          <Button
            onClick={() => navigate("/projects/new")}
            size="sm"
          >
            <PlusIcon /> New project
          </Button>
        </div>
      </PageHeader>
      <PageBody width="wide">
        {projects.isLoading ? (
          <ProjectGridSkeleton />
        ) : projects.isError ? (
          <Alert variant="destructive">
            <AlertDescription>
              Could not load projects: {projects.error.message}
            </AlertDescription>
          </Alert>
        ) : items.length === 0 ? (
          <Empty className="rounded-xl border bg-card">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <BugIcon />
              </EmptyMedia>
              <EmptyTitle>No projects yet</EmptyTitle>
              <EmptyDescription>
                Create your first project to start receiving bug reports from
                your game.
              </EmptyDescription>
            </EmptyHeader>
            <EmptyContent>
              <Button onClick={() => navigate("/projects/new")}>
                <PlusIcon /> New project
              </Button>
            </EmptyContent>
          </Empty>
        ) : (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
            {items.map((p) => (
              <ProjectCard key={p.id} project={p} />
            ))}
          </div>
        )}
      </PageBody>
    </Page>
  );
}

function ProjectGridSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
      {Array.from({ length: 3 }).map((_, idx) => (
        <Skeleton key={idx} className="h-32 w-full" />
      ))}
    </div>
  );
}

function ProjectCard({ project }: { project: Project }) {
  return (
    <Link
      to={`/projects/${project.id}`}
      className="block rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      data-testid={`project-card-${project.id}`}
    >
      <Card>
      <CardHeader>
        <div className="flex flex-col gap-1.5">
          <CardTitle className="flex items-center gap-2">
            <span
              className="inline-block size-3 shrink-0 rounded-full"
              style={{ background: project.color }}
              aria-hidden="true"
            />
            {project.name}
          </CardTitle>
          <CardDescription className="font-mono uppercase tracking-wide">
            {project.key}
          </CardDescription>
        </div>
        <CardAction>
          <Badge variant="secondary" className="text-success">active</Badge>
        </CardAction>
      </CardHeader>
      <CardContent>
        <p className="line-clamp-2 text-sm text-muted-foreground">
          {project.description || "No description yet."}
        </p>
      </CardContent>
      <CardFooter>
        <span className="font-mono text-xs text-muted-foreground">
          created {shortDate(project.createdAt)}
        </span>
      </CardFooter>
      </Card>
    </Link>
  );
}
